import { Box, CircularProgress, Typography } from '@mui/material';
import React, { Suspense, lazy } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { LoadingProvider } from './components/OptimizedLoader/OptimizedLoader';
import { AuthProvider } from './contexts/AuthContext';
import { ExamProvider } from './contexts/ExamContext';
import { OfflineProvider } from './contexts/OfflineContext';
import { OwnershipProvider } from './contexts/OwnershipContext';
import { PromotionalProvider } from './contexts/PromotionalContext';
import { supabase } from './lib/supabaseClient';

// ✅ Lazy Loaded Components (Optimizes initial load for 2G/3G)
const Layout = lazy(() => import('./components/Layout/Layout'));
const Login = lazy(() => import('./pages/auth/Login'));
const Register = lazy(() => import('./pages/auth/Register'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard'));
const Profile = lazy(() => import('./pages/Profile/Profile'));
const TestList = lazy(() => import('./components/TestList/TestList'));
const Study = lazy(() => import('./pages/Study/Study'));
const BookmarksList = lazy(() => import('./pages/Bookmarks/BookmarksList'));
const OfflineLibrary = lazy(() => import('./pages/StudyMaterial/OfflineLibrary'));
const CompleteProfile = lazy(() => import('./pages/auth/CompleteProfile'));
const OverallAnalytics = lazy(() => import('./pages/analytics/OverallAnalytics'));
const NotFound = lazy(() => import('./pages/NotFound'));
const PrivateRoute = lazy(() => import('./components/PrivateRoute'));
const StudyMaterialContainer = lazy(
  () => import('./components/StudyMaterialDesign/StudyMaterialContainer')
);
const AnnouncementsList = lazy(() => import('./components/Announcements/AnnouncementsList'));
const CurrentAffairsFeed = lazy(() => import('./pages/CurrentAffairs/CurrentAffairsFeed'));
const TypingTest = lazy(() => import('./pages/TypingTest/TypingTest'));
const LearnHome = lazy(() => import('./features/learn/LearnHome'));
const LearnTablePage = lazy(() => import('./features/learn/LearnTablePage'));
const LearnSectionPage = lazy(() => import('./features/learn/LearnSectionPage'));
const PathwayPage = lazy(() => import('./features/learn/PathwayPage'));
const FormulaSetPage = lazy(() => import('./features/learn/FormulaSetPage'));
const RevisePage = lazy(() => import('./features/learn/RevisePage'));
const DiagramPage = lazy(() => import('./features/learn/DiagramPage'));
const LabRouter = lazy(() => import('./features/learn/labs/LabRouter'));

// Test Engines & Viewers
const RouteTestEngine = lazy(() => import('./pages/Test/RouteTestEngine'));
const ViewLastResults = lazy(() => import('./pages/Test/ViewLastResults'));
const UniversalTestInterface = lazy(() => import('./pages/Test/UniversalTestInterface'));
const Premium = lazy(() => import('./pages/Premium/Premium'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));
const VerifyEmail = lazy(() => import('./pages/auth/VerifyEmail'));

// Contest Pages
const ContestComingSoon = lazy(() => import('./pages/Contest/ContestComingSoon'));
const ContestDetails = lazy(() => import('./pages/Contest/ContestDetails'));
const ContestTestRunner = lazy(() => import('./pages/Contest/ContestTestRunner'));
const ContestAnalytics = lazy(() => import('./pages/Contest/ContestAnalytics'));
const ContestLeaderboard = lazy(() => import('./pages/Contest/ContestLeaderboard'));

// Study Material Viewers
const PDFViewer = lazy(() => import('./components/StudyMaterialDesign/viewers/PDFViewer.jsx'));
const VideoPlayer = lazy(() => import('./components/StudyMaterialDesign/viewers/VideoPlayer'));
const AudioPlayer = lazy(() => import('./components/StudyMaterialDesign/viewers/AudioPlayer'));
const TextReader = lazy(() => import('./components/StudyMaterialDesign/viewers/TextReader'));
const FlashcardViewer = lazy(
  () => import('./components/StudyMaterialDesign/viewers/FlashcardViewer')
);
const StudyMaterialViewer = lazy(
  () => import('./components/StudyMaterialDesign/viewers/StudyMaterialViewer')
);

// Simple loading fallback
const PageLoader = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      bgcolor: '#fff',
    }}
  >
    <CircularProgress size={40} thickness={4} sx={{ color: '#1976D2' }} />
  </Box>
);

// Impersonation auto-login handler component
function ImpersonateHandler() {
  const params = new URLSearchParams(window.location.search);
  React.useEffect(() => {
    const run = async () => {
      const token = params.get('token');
      const userId = params.get('user_id');
      const exp = params.get('exp');
      if (token && userId) {
        let decoded = {};
        try {
          decoded = JSON.parse(atob(decodeURIComponent(token)));
        } catch (e) {
          console.warn('Token decode error:', e);
        }

        let realName = 'Student';
        let realEmail = decoded.user_email || '';
        try {
          const { data: prof } = await supabase
            .from('user_profiles')
            .select('*')
            .eq('user_id', userId)
            .maybeSingle();
          if (prof) {
            if (prof.full_name) realName = prof.full_name;
            if (prof.email) realEmail = prof.email;
          }
        } catch (e) {
          console.warn('Profile fetch error:', e);
        }

        const userObj = {
          id: userId,
          email: realEmail || 'student@Prepgrind.com',
          user_metadata: { full_name: realName },
          role: 'authenticated',
          email_confirmed_at: new Date().toISOString(),
        };
        const sessObj = {
          access_token: token,
          refresh_token: token,
          user: userObj,
          expires_at: Math.floor(Date.now() / 1000) + 600,
        };
        localStorage.setItem('sb_session', JSON.stringify(sessObj));
        localStorage.setItem(
          'active_impersonation_session',
          JSON.stringify({
            token,
            userId,
            exp,
            adminEmail: decoded.admin || 'Admin',
            isImpersonated: true,
            userName: realName,
            userEmail: realEmail,
          })
        );
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 400);
      }
    };
    run();
  }, [params]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        bgcolor: '#f4f4f5',
      }}
    >
      <CircularProgress sx={{ color: '#D97706', mb: 2 }} />
      <Typography variant="h6" fontWeight={700}>
        Initializing 10-Minute Admin Debug Session...
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Loading real student profile & redirecting to dashboard...
      </Typography>
    </Box>
  );
}

function App() {
  // v7_startTransition: keep the current screen (and any closing dropdown) visible while the next
  // page's code loads, instead of swapping to the loader mid-animation and leaving the dropdown stuck
  return (
    <Router future={{ v7_startTransition: true }}>
      <AuthProvider>
        <ExamProvider>
          <OwnershipProvider>
            <PromotionalProvider>
              <OfflineProvider>
                <LoadingProvider>
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      {/* Public Routes */}
                      <Route path="/impersonate" element={<ImpersonateHandler />} />
                      <Route path="/login" element={<Login />} />
                      <Route path="/register" element={<Register />} />
                      <Route path="/signup" element={<Register />} />
                      <Route path="/signup/:categoryParam" element={<Register />} />
                      <Route path="/signup/:categoryParam/:examParam" element={<Register />} />
                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route path="/auth/reset" element={<ResetPassword />} />
                      <Route path="/register/verify-email" element={<VerifyEmail />} />
                      <Route
                        path="/complete-profile"
                        element={
                          <PrivateRoute>
                            <CompleteProfile />
                          </PrivateRoute>
                        }
                      />

                      {/* Protected Routes */}
                      <Route
                        element={
                          <PrivateRoute>
                            <Layout />
                          </PrivateRoute>
                        }
                      >
                        <Route path="/" element={<Navigate to="/dashboard" replace />} />
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/topic-wise" element={<TestList testType="practice" />} />
                        <Route path="/dpp" element={<TestList testType="dpp" />} />
                        <Route path="/chapter-wise" element={<TestList testType="quiz" />} />
                        <Route path="/section-wise" element={<TestList testType="sectional" />} />
                        <Route path="/mock" element={<TestList testType="mock" />} />
                        <Route path="/study" element={<Study />} />

                        <Route path="/study-material" element={<StudyMaterialContainer />} />
                        <Route
                          path="/study-material/:section"
                          element={<StudyMaterialContainer />}
                        />

                        <Route path="/study-material/pdf/:id" element={<StudyMaterialViewer />} />
                        <Route path="/study-material/video/:id" element={<StudyMaterialViewer />} />
                        <Route path="/study-material/audio/:id" element={<StudyMaterialViewer />} />
                        <Route path="/study-material/text/:id" element={<StudyMaterialViewer />} />
                        <Route
                          path="/study-material/viewer/:id"
                          element={<StudyMaterialViewer />}
                        />
                        <Route
                          path="/study-material/preview/viewer"
                          element={<StudyMaterialViewer />}
                        />

                        <Route path="/contests" element={<ContestComingSoon />} />
                        <Route path="/contests/:contestId" element={<ContestDetails />} />
                        <Route path="/contests/:contestId/test" element={<ContestTestRunner />} />
                        <Route
                          path="/contests/:contestId/play/:testId"
                          element={<UniversalTestInterface />}
                        />
                        <Route
                          path="/contests/:contestId/analytics/:testId"
                          element={<ContestAnalytics />}
                        />
                        <Route
                          path="/contests/:contestId/leaderboard"
                          element={<ContestLeaderboard />}
                        />

                        <Route path="/bookmarks" element={<BookmarksList />} />
                        <Route path="/offline-library" element={<OfflineLibrary />} />
                        <Route path="/analytics" element={<OverallAnalytics />} />
                        <Route path="/profile" element={<Profile />} />
                        <Route path="/premium" element={<Premium />} />
                        <Route path="/announcements" element={<AnnouncementsList />} />
                        <Route path="/current-affairs" element={<CurrentAffairsFeed />} />
                        <Route path="/typing-test" element={<TypingTest />} />
                        <Route path="/learn" element={<LearnHome />} />
                        <Route path="/learn/revise" element={<RevisePage />} />
                        <Route path="/learn/labs/:labId" element={<LabRouter />} />
                        <Route path="/learn/:packId/s/:sectionId" element={<LearnSectionPage />} />
                        <Route path="/learn/:packId/p/:pathwayId" element={<PathwayPage />} />
                        <Route path="/learn/:packId/f/:setId" element={<FormulaSetPage />} />
                        <Route path="/learn/:packId/d/:diagramId" element={<DiagramPage />} />
                        <Route path="/learn/:packId/:tableId" element={<LearnTablePage />} />
                        <Route path="/typing" element={<Navigate to="/typing-test" replace />} />
                        <Route
                          path="/dashboard/announcements"
                          element={<Navigate to="/announcements" replace />}
                        />

                        <Route path="/test/:testId" element={<RouteTestEngine />} />
                        <Route path="/test/:testId/results" element={<ViewLastResults />} />
                      </Route>

                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </LoadingProvider>
              </OfflineProvider>
            </PromotionalProvider>
          </OwnershipProvider>
        </ExamProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
